/**
 * Architectural measurements for the selected room in the assembler: six
 * measure bars, three per axis (gap from the level's minimum bound, the
 * room's own extent, gap to the maximum bound) over the union bounds of the
 * level's rooms. The gaps are editable (moving the room, never resizing it);
 * the extents are read-only.
 */
import { type PlanBounds, roomAssemblyBounds, type RoomPlacement, unionPlanBounds } from '@kaiizen/kai-floor-plan';
import {
  type BarScreen,
  barsOnStage,
  type DimBarSides,
  dimBarSidesFor,
  gapSizeGapBars,
  type MeasureBar,
  measureBarScreen,
  type PlacedBar,
} from '../editor-model';
import { type AssemblerMutableState, levelBounds, levelRooms, selectedAssemblyRoom } from './assemblerState';
import { placeAssemblyRoom } from './assemblyModel';

/** Chip keys of the four editable gaps. */
export const ASSEMBLY_GAP_KEYS = ['agap-left', 'agap-right', 'agap-top', 'agap-bottom'] as const;
export type AssemblyGapKey = typeof ASSEMBLY_GAP_KEYS[number];

/** Whether a chip key is one of the four editable gaps. */
export const isAssemblyGapKey = (key: string): key is AssemblyGapKey => (ASSEMBLY_GAP_KEYS as readonly string[]).includes(key);

/** A gap never shows negative: a room outside the level's bounds reads as flush. */
const clampedGap = (gap: number): number => Math.max(0, gap);

/** Six bars for the selected room, or none when nothing is selected. */
export function computeAssemblyDims(st: AssemblerMutableState): MeasureBar[] {
  const selected = selectedAssemblyRoom(st);
  const region = levelBounds(st);
  if (!selected || !region) return [];
  return gapSizeGapBars(
    roomAssemblyBounds(selected.room),
    region,
    {
      left: 'agap-left',
      width: `aroom:${selected.id}:width`,
      right: 'agap-right',
      top: 'agap-top',
      length: `aroom:${selected.id}:length`,
      bottom: 'agap-bottom',
    },
    clampedGap,
  );
}

/** Bars sit on the side of the region nearest the selected room (default top/left). */
export function assemblyDimBarSides(st: AssemblerMutableState): DimBarSides {
  const selected = selectedAssemblyRoom(st);
  const bounds = selected && roomAssemblyBounds(selected.room);
  const center = bounds ? { x: (bounds.minX + bounds.maxX) / 2, y: (bounds.minY + bounds.maxY) / 2 } : null;
  return dimBarSidesFor(center, levelBounds(st));
}

export function assemblyBarScreen(st: AssemblerMutableState, bar: MeasureBar): BarScreen {
  return measureBarScreen(st.viewport, levelBounds(st) ?? { minX: 0, minY: 0, maxX: 0, maxY: 0 }, assemblyDimBarSides(st), bar, st.inputKind);
}

/**
 * The selected room's measure bars where they draw on a `stageW` × `stageH`
 * stage, each family held on it (`barsOnStage`). The bar lines and their
 * chips both draw from this, so they move together while the view is zoomed in.
 */
export function placedAssemblyBars(st: AssemblerMutableState, stageW: number, stageH: number): PlacedBar[] {
  return barsOnStage(computeAssemblyDims(st).map(bar => ({ bar, g: assemblyBarScreen(st, bar) })), stageW, stageH);
}

/** The level's bounds without the selected room: the reference a gap edit is measured from. */
function otherRoomsBounds(st: AssemblerMutableState, selectedId: string): PlanBounds | null {
  return unionPlanBounds(levelRooms(st).filter(room => room.id !== selectedId).map(room => roomAssemblyBounds(room.room)));
}

/**
 * Commit a dim chip edit. Only the four gaps are editable: the room moves so
 * its bounds sit `value` feet from the other rooms' bounds on that side (the
 * room's own extent never changes). A lone room on its level has no reference
 * to measure from, so its gaps are inert.
 */
export function applyAssemblyChipEdit(st: AssemblerMutableState, key: string, value: number): void {
  const selected = selectedAssemblyRoom(st);
  if (!selected || st.readOnly) return;
  const others = otherRoomsBounds(st, selected.id);
  if (!others) return;
  const gap = Math.max(0, value);
  const b = roomAssemblyBounds(selected.room);
  const { x, y } = { x: selected.room.x ?? 0, y: selected.room.y ?? 0 };
  let patch: Partial<RoomPlacement> | null = null;
  if (key === 'agap-left') patch = { x: x + (others.minX + gap - b.minX) };
  else if (key === 'agap-right') patch = { x: x + (others.maxX - gap - b.maxX) };
  else if (key === 'agap-top') patch = { y: y + (others.minY + gap - b.minY) };
  else if (key === 'agap-bottom') patch = { y: y + (others.maxY - gap - b.maxY) };
  if (patch) st.rooms = placeAssemblyRoom(st.rooms, selected.id, patch);
}

/** Commit a Details-panel placement field for the selected room. */
export function applyAssemblyPlacementInput(st: AssemblerMutableState, patch: Partial<RoomPlacement>): void {
  const selected = selectedAssemblyRoom(st);
  if (!selected || st.readOnly) return;
  st.rooms = placeAssemblyRoom(st.rooms, selected.id, patch);
  // Moving a room to another level: follow it, so the edit stays visible.
  if (patch.level != null && patch.level !== st.level) st.level = Math.round(patch.level);
}
